import type { ReactNode } from 'react';

/**
 * Peças soltas de interface, usadas pelas telas dentro do workspace.
 *
 * A moldura em si vive em `workspace/`: o menu de cima que morava aqui saiu
 * junto com as páginas soltas.
 */

export function Cartao({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="rounded-xl bg-zinc-900/50">
      <div className="flex items-center justify-between border-b border-zinc-800/70 px-5 py-3">
        <h2 className="font-medium text-zinc-100">{titulo}</h2>
        {acao}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Selo({ ligada }: { ligada: boolean }): React.JSX.Element {
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs ' +
        (ligada ? 'bg-emerald-500/10 text-emerald-300' : 'bg-zinc-800 text-zinc-400')
      }
    >
      <span className={'h-1.5 w-1.5 rounded-full ' + (ligada ? 'bg-emerald-400' : 'bg-zinc-600')} />
      {ligada ? 'no ar' : 'desligada'}
    </span>
  );
}

/**
 * Botão de um glifo só, para barras onde a moldura já é de outro elemento.
 *
 * Sem borda de propósito: dentro da caixa do compositor, um botão com contorno
 * próprio faz a barra parecer remendo colado. O rótulo é obrigatório e vira
 * `title` e `aria-label` ao mesmo tempo — ícone sem nome é enigma para quem lê
 * a tela e para quem passa o mouse.
 *
 * No dedo ele cresce para 44 px. Vinte e oito pixels é uma medida de ponteiro:
 * o ensaio de celular mediu dezenove alvos abaixo de quarenta na conversa, e
 * o pior deles tinha 10 px de largura. O `pointer-coarse` separa as duas
 * mãos sem duplicar componente — no desktop nada muda.
 *
 * `miudo` desfaz esse crescimento. Serve ao compositor recolhido, que é uma
 * barra de descanso e não de trabalho: ali os ícones ficam discretos de
 * propósito, e quem vai usá-los toca no campo primeiro — o que abre a barra
 * inteira, já em tamanho de dedo.
 */
export function BotaoIcone({
  rotulo,
  ligado = false,
  miudo = false,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  rotulo: string;
  /** Aceso enquanto o que ele abre está aberto. */
  ligado?: boolean;
  /** Fica pequeno mesmo no dedo. Só para barra recolhida. */
  miudo?: boolean;
}): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'button'}
      title={props.title ?? rotulo}
      aria-label={rotulo}
      aria-pressed={ligado}
      className={
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm transition ' +
        (miudo ? '' : 'pointer-coarse:h-11 pointer-coarse:w-11 pointer-coarse:text-base ') +
        'hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40 ' +
        (ligado ? 'bg-zinc-800 text-zinc-200' : 'text-zinc-400')
      }
    >
      {children}
    </button>
  );
}

/**
 * Botão secundário, para ações dentro de uma lista.
 *
 * `min-h` em vez de `py` maior no toque: a altura sobe para 44 px sem inchar o
 * texto nem desalinhar o botão do vizinho quando um deles quebra em duas
 * linhas. Medido: "Remover", "Desativar" e "Gerar credencial" saíam com 26 px
 * de altura no telefone — metade do que um polegar acerta.
 */
export function BotaoLeve({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'button'}
      className={
        'inline-flex items-center justify-center rounded-md border border-zinc-700 px-2.5 py-1 ' +
        'text-xs text-zinc-300 transition hover:bg-zinc-800 disabled:opacity-40 ' +
        'pointer-coarse:min-h-11 pointer-coarse:px-3 pointer-coarse:text-sm'
      }
    >
      {children}
    </button>
  );
}
